import React from "react";
import { X, Zap, AlertCircle, Check, Sparkles } from "lucide-react";
import CustomActionDropdown from "./Customactiondropdown";
import ActionFormFields from "./ActionFormFields";
import { isActionBlockedForManagerView } from "../Constant/EmployeeActions.config";
import { normalizePermissionFeatureName } from "../../../utils/permissionNormalization";

const ActionModal = ({
  t,
  styles,
  theme,
  isRTL,
  language,
  selectedEmployee,
  selectedAction,
  setSelectedAction,
  isDropdownOpen,
  setIsDropdownOpen,
  formData,
  setFormData,
  errorMsg,
  setErrorMsg,
  departments,
  departmentManagers,
  getEmployeeName,
  handleInputChange,
  handleDropdownChange,
  handleSubmit,
  permissions,
  isAdminView,
  isAdminRole,
  hasFeatureAccess,
  ACTION_TYPES,
  ACTION_FEATURE_MAP,
  workflowDefinitions,
  onClose,
}) => {
  const featureKey = ACTION_FEATURE_MAP[selectedAction] || null;
  // hasFeatureAccess (AuthContext) does a resilient lookup — exact key match,
  // then a normalized fallback across permissions.features — where this
  // component's old hand-rolled check only ever read the exact
  // "changesPage" property and silently failed closed on any mismatch,
  // which is why some granted actions never appeared.
  const isActionAllowedForAdmin =
    !isActionBlockedForManagerView(selectedAction, { isAdminView, isAdminRole }) &&
    (!isAdminView ||
    isAdminRole ||
    !hasFeatureAccess ||
    !featureKey ||
    hasFeatureAccess("changesPage", featureKey));

  return (
    <div style={styles.modal} onClick={onClose}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <div
            style={{
              display: "flex",
              flexDirection: isRTL ? "row" : "row",
              alignItems: "center",
            }}
          >
            <div
              style={{
                ...styles.modalIconWrapper,
                background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
              }}
            >
              <Zap size={26} color="white" />
            </div>
            <h2 style={styles.modalTitle}>{t.employeeAction}</h2>
          </div>
        </div>

        {selectedEmployee && (
          <div style={styles.infoBox}>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>{t.employeeName}</span>
              <span style={styles.infoValue}>
                {getEmployeeName(selectedEmployee)}
              </span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>{t.infinityCode}</span>
              <span style={styles.infoValue}>
                {selectedEmployee.clientCode || selectedEmployee.infinityCode || t.notAvailable}
              </span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>{t.currentPosition}</span>
              <span style={styles.infoValue}>
                {selectedEmployee.position ?? selectedEmployee.employeeType ?? t.notAvailable}
              </span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>{t.department}</span>
              <span style={styles.infoValue}>
                {selectedEmployee.department?.name ?? selectedEmployee.employeeType ?? t.notAvailable}
              </span>
            </div>
            {/* Salary is admin / admin-level information — hidden in manager view. */}
            {(isAdminView || isAdminRole) && (
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>{t.currentSalary}</span>
                <span style={styles.infoValue}>
                  {selectedEmployee.netSalary
                    ? `${t.egp} ${selectedEmployee.netSalary.toLocaleString()}`
                    : t.notAvailable}
                </span>
              </div>
            )}
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>{t.performanceRating}</span>
              <span style={styles.infoValue}>
                {selectedEmployee.performance || t.notAvailable}
              </span>
            </div>
            <div
              style={{
                ...styles.infoRow,
                borderBottom: "none",
                marginBottom: 0,
                paddingBottom: 0,
              }}
            >
              <span style={styles.infoLabel}>{t.reportsTo}</span>
              <span style={styles.infoValue}>
                {selectedEmployee.firstLineManagerName || t.notAvailable}
              </span>
            </div>
          </div>
        )}

        {errorMsg && (
          <div
            style={{ ...styles.alertBox, ...styles.errorAlert }}
            className="fade-in-alert"
          >
            <AlertCircle size={20} />
            <span>{errorMsg}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={styles.controlGroup}>
            <label style={styles.label}>
              <Sparkles size={15} />
              {t.actionType} *
            </label>
            <CustomActionDropdown
              t={t}
              styles={styles}
              theme={theme}
              isRTL={isRTL}
              selectedAction={selectedAction}
              isDropdownOpen={isDropdownOpen}
              setIsDropdownOpen={setIsDropdownOpen}
              setSelectedAction={setSelectedAction}
              setErrorMsg={setErrorMsg}
              permissions={permissions}
              isAdminView={isAdminView}
              isAdminRole={isAdminRole}
              hasFeatureAccess={hasFeatureAccess}
              ACTION_TYPES={ACTION_TYPES}
              ACTION_FEATURE_MAP={ACTION_FEATURE_MAP}
              workflowDefinitions={workflowDefinitions}
              language={language}
              departmentName={selectedEmployee?.department?.name ?? selectedEmployee?.employeeType ?? null}
            />
          </div>

          <ActionFormFields
            t={t}
            styles={styles}
            theme={theme}
            isRTL={isRTL}
            language={language}
            selectedAction={selectedAction}
            formData={formData}
            handleInputChange={handleInputChange}
            handleDropdownChange={handleDropdownChange}
            departments={departments}
            departmentManagers={departmentManagers}
            selectedEmployee={selectedEmployee}
            ACTION_TYPES={ACTION_TYPES}
            workflowDefinitions={workflowDefinitions}
            hideSalary={!isAdminView && !isAdminRole}
          />

          <div
            style={{
              display: "flex",
              flexDirection: isRTL ? "row" : "row",
              gap: "14px",
              marginTop: "clamp(28px, 4.5vw, 36px)",
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              style={{ ...styles.secondaryButton, flex: 1 }}
              onClick={onClose}
            >
              <X size={18} /> {t.cancel}
            </button>
            {isActionAllowedForAdmin && (
              <button
                type="submit"
                style={{ ...styles.primaryButton, flex: 1 }}
                disabled={!selectedAction}
              >
                <Check size={18} /> {t.submitAction}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default ActionModal;
